import {useEffect, useMemo, useState} from 'react'
import {BASE_URL} from '../../../api'
import config from '../../config'
const {axios} = config

// Roadmap #9 (2026-09-23): control the order practitioners/businesses appear
// in each category listing of the app, per town (e.g. Tulum -> Yoga
// Instructors). Drag rows (or use the arrows) and Save - it's live in the
// app right away, no app release needed. Anyone not placed yet is shown
// after the placed ones, newest account first; new practitioners appear
// here as "not placed" until you save the list again.
const ListingOrder = () => {
  const [options, setOptions] = useState({locations: [], categories: []})
  const [location, setLocation] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [subcategoryId, setSubcategoryId] = useState('')
  const [rows, setRows] = useState([])
  const [updatedAt, setUpdatedAt] = useState(null)
  const [dirty, setDirty] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState(null) // {type: 'success'|'danger', text}
  const [dragIndex, setDragIndex] = useState(null)

  useEffect(() => {
    axios
      .get(`${BASE_URL}/admin/listing-order/options`)
      .then(({data}) => setOptions(data))
      .catch(() => setNotice({type: 'danger', text: 'Could not load towns and categories.'}))
  }, [])

  const subcategories = useMemo(
    () => options.categories.find((c) => c._id === categoryId)?.subcategories || [],
    [options, categoryId]
  )

  useEffect(() => {
    setRows([])
    setDirty(false)
    setUpdatedAt(null)
    if (!location || !subcategoryId) return
    setLoading(true)
    axios
      .get(`${BASE_URL}/admin/listing-order`, {params: {location, subcategory: subcategoryId}})
      .then(({data}) => {
        setRows(data.users || [])
        setUpdatedAt(data.updatedAt)
      })
      .catch(() => setNotice({type: 'danger', text: 'Could not load this listing.'}))
      .finally(() => setLoading(false))
  }, [location, subcategoryId])

  const move = (from, to) => {
    if (to < 0 || to >= rows.length || from === to) return
    const next = [...rows]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    setRows(next)
    setDirty(true)
  }

  const save = async (order) => {
    setSaving(true)
    setNotice(null)
    try {
      const {data} = await axios.put(`${BASE_URL}/admin/listing-order`, {
        location,
        subcategory: subcategoryId,
        order,
      })
      setUpdatedAt(data.updatedAt)
      setRows((r) => r.map((u) => ({...u, placed: order.includes(u._id)})))
      setDirty(false)
      setNotice({
        type: 'success',
        text: order.length
          ? 'Order saved - it is live in the app now.'
          : 'Custom order cleared - this listing now shows newest first.',
      })
    } catch (err) {
      setNotice({type: 'danger', text: 'Could not save. Please try again.'})
    } finally {
      setSaving(false)
    }
  }

  const unplacedCount = rows.filter((r) => !r.placed).length

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Listing Order</h3>
        </div>
      </div>

      <div className='card-body border-top p-9'>
        <p className='text-muted mb-8'>
          Choose a town and category, then drag practitioners and businesses into the order you
          want them shown in the app. Saving takes effect immediately - no app release needed.
          Anyone you haven't placed yet appears after your list, newest first.
        </p>

        <div className='row mb-8 g-4'>
          <div className='col-md-4'>
            <label className='form-label fw-bold'>Town</label>
            <select
              className='form-select form-select-solid'
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            >
              <option value=''>Select a town</option>
              {options.locations.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div className='col-md-4'>
            <label className='form-label fw-bold'>Category</label>
            <select
              className='form-select form-select-solid'
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value)
                setSubcategoryId('')
              }}
            >
              <option value=''>Select a category</option>
              {options.categories.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className='col-md-4'>
            <label className='form-label fw-bold'>Subcategory</label>
            <select
              className='form-select form-select-solid'
              value={subcategoryId}
              disabled={!categoryId}
              onChange={(e) => setSubcategoryId(e.target.value)}
            >
              <option value=''>Select a subcategory</option>
              {subcategories.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {notice && <div className={`alert alert-${notice.type} py-3`}>{notice.text}</div>}

        {location && subcategoryId && (
          <>
            <div className='d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3'>
              <div className='text-muted fs-7'>
                {loading
                  ? 'Loading...'
                  : `${rows.length} listed` +
                    (updatedAt
                      ? ` - order last saved ${new Date(updatedAt).toLocaleString()}`
                      : ' - no custom order saved yet (showing newest first)') +
                    (updatedAt && unplacedCount ? ` - ${unplacedCount} not placed yet` : '')}
              </div>
              <div className='d-flex gap-3'>
                {updatedAt && (
                  <button
                    type='button'
                    className='btn btn-light btn-sm'
                    disabled={saving}
                    onClick={() => {
                      if (window.confirm('Clear the custom order for this listing?')) save([])
                    }}
                  >
                    Clear custom order
                  </button>
                )}
                <button
                  type='button'
                  className='btn btn-primary btn-sm'
                  disabled={saving || loading || !rows.length || (!dirty && unplacedCount === 0)}
                  onClick={() => save(rows.map((r) => r._id))}
                >
                  {saving ? 'Saving...' : 'Save order'}
                </button>
              </div>
            </div>

            {!loading && rows.length === 0 && (
              <div className='text-muted py-10 text-center'>
                No one is listed in this category in this town yet.
              </div>
            )}

            <ul className='list-unstyled m-0'>
              {rows.map((u, i) => (
                <li
                  key={u._id}
                  draggable
                  onDragStart={() => setDragIndex(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    move(dragIndex, i)
                    setDragIndex(null)
                  }}
                  onDragEnd={() => setDragIndex(null)}
                  className='d-flex align-items-center border rounded p-3 mb-2 bg-body'
                  style={{cursor: 'grab', opacity: dragIndex === i ? 0.5 : 1}}
                >
                  <span className='text-gray-400 fs-2 me-3' aria-hidden='true'>
                    ⠿
                  </span>
                  <span className='fw-bold text-gray-600 me-4' style={{width: 24}}>
                    {i + 1}
                  </span>
                  <img
                    src={u.profileImageUrl || '/media/svg/avatars/blank.svg'}
                    alt=''
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      marginRight: 12,
                    }}
                  />
                  <div className='flex-grow-1'>
                    <div className='fw-bold'>{u.name}</div>
                    <div className='text-muted fs-8'>
                      {u.role === 'BUSINESS' ? 'Business' : 'Practitioner'}
                      {!u.placed && updatedAt && (
                        <span className='badge badge-light-warning ms-2'>Not placed yet</span>
                      )}
                    </div>
                  </div>
                  <button
                    type='button'
                    className='btn btn-icon btn-sm btn-light me-2'
                    aria-label='Move up'
                    disabled={i === 0}
                    onClick={() => move(i, i - 1)}
                  >
                    ↑
                  </button>
                  <button
                    type='button'
                    className='btn btn-icon btn-sm btn-light'
                    aria-label='Move down'
                    disabled={i === rows.length - 1}
                    onClick={() => move(i, i + 1)}
                  >
                    ↓
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

export default ListingOrder
